import { Info, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { BrandSummaryResponse } from "@/types";
import { formatSigned, formatSignedPct } from "./utils";

interface MetricCardsProps {
  summary: BrandSummaryResponse;
}

export function MetricCards({ summary }: MetricCardsProps) {
  return (
    <div className="grid grid-cols-3 gap-3 mb-4">
      <Card>
        <CardContent className="p-4 space-y-1">
          <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
            Avg Brand Rank Δ (30d)
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/60 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px]">
                Compares average rank from the first 7 days vs. the last 7 days of the 30-day window. A positive number means rank improved (moved closer to #1).
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">
              {formatSigned(summary.avgRankDelta)}
            </span>
            {summary.avgRankDelta != null && (
              summary.avgRankDelta > 0 ? (
                <TrendingUp className="h-4 w-4 text-trend-positive" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {summary.avgRankDelta == null
              ? "no data"
              : `positions ${summary.avgRankDelta > 0 ? "improved" : "declined"}`}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 space-y-1">
          <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
            Units Sold Δ
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/60 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px]">
                Percentage change in average daily units sold, comparing the first 7 days to the last 7 days of the 30-day window.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">
              {formatSignedPct(summary.unitsDeltaPct)}
            </span>
            {summary.unitsDeltaPct != null && (
              summary.unitsDeltaPct > 0 ? (
                <TrendingUp className="h-4 w-4 text-trend-positive" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            avg/day (last 7d)
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 space-y-1">
          <div className="text-xs text-muted-foreground font-medium flex items-center gap-1">
            BSR Δ
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/60 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px]">
                Percentage improvement in Best Seller Rank. A positive value means BSR improved (moved closer to #1).
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">
              {formatSignedPct(summary.bsrDeltaPct)}
            </span>
            {summary.bsrDeltaPct != null && (
              summary.bsrDeltaPct > 0 ? (
                <TrendingUp className="h-4 w-4 text-trend-positive" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            avg (last 7d)
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
